<template>
  <div class="matrix-cell"
       @click="$emit('click')">

    <div class="cell-inner">
      <div class="cell-color-bar" :style="colorBarStyle"></div>

      <!-- KPI Mode: Zeigt den KPI-Wert mit Farbhinterlegung und Delta -->
      <div v-if="mode === 'kpi'" class="cell-content">
        <div v-if="item?.value !== null" class="cell-value">
          {{ item?.value }}<span class="value-unit">{{ item.value !== '\u00A0' ? '%' : '\u00A0' }}</span>
        </div>
        <div v-else class="cell-value cell-empty">—</div>
        <div v-if="typeof item?.delta === 'number'" class="cell-delta" :class="getDeltaClass(item?.delta)">
          <Triangle v-if="item.delta > 0" :size="6" class="delta-triangle triangle-up" />
          <Triangle v-else-if="item.delta < 0" :size="6" class="delta-triangle triangle-down" />
          {{ item.delta > 0 ? '+' : '' }}{{ item.delta }}
        </div>
        <div v-else-if="item?.delta === null && item?.value !== '\u00A0'" class="cell-delta delta-neutral">N/A</div>
        <div v-else class="cell-delta">&nbsp;</div>
      </div>

      <!-- Question Mode: Zeigt nur die Anzahl n an -->
      <div v-else-if="mode === 'question'" class="cell-content">
        <div v-if="item?.n !== null && item?.n !== '\u00A0'" class="cell-meta">
          n={{ item?.n }}
        </div>
      </div>

      <!-- Answer Mode: Zeigt den Antwortwert mit n -->
      <div v-else-if="mode === 'answer'" class="cell-content">
        <div v-if="item?.value !== null" class="cell-value">
          {{ item?.value }}<span class="value-unit">{{ item.value !== '\u00A0' ? '%' : '\u00A0' }}</span>
        </div>
        <div v-else class="cell-value cell-empty">—</div>
        <div v-if="item?.n !== null" class="cell-meta">
          {{ item?.n }}
        </div>
      </div>

      <!-- Fallback für unbekannte Modi -->
      <div v-else class="cell-content">
        <span class="text-muted">&nbsp;</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Triangle } from 'lucide-vue-next';

export default {
  name: 'MatrixCell',

  components: {
    Triangle
  },

  props: {
    mode:        { type: String, default: 'kpi' },
    highlighted: { type: Boolean, default: false },
    item:        { type: Object, default: null }
  },

  computed: {
    colorBarStyle() {
      const hasValue = this.mode === 'kpi' && typeof this.item?.value === 'number';
      if (hasValue && this.item?.color) {
        return { backgroundColor: this.item.color };
      }
      return { backgroundColor: 'transparent' };
    }
  },

  methods: {
    getDeltaClass(delta) {
      if (delta > 0) return 'delta-positive';
      if (delta < 0) return 'delta-negative';
      return 'delta-neutral';
    },
  }
};
</script>

<style lang="scss" scoped>
.matrix-cell {
  min-width: 5rem;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  border-right: 1px solid var(--bs-border-color);
}

.matrix-cell:last-child {
  border-right: none;
}

.matrix-cell:hover {
  background-color: color-mix(in srgb, var(--bs-border-color) 10%, transparent);
}

.cell-inner {
  display: flex;
  align-items: stretch;
  gap: 4px;
}

.cell-color-bar {
  width: 4px;
  flex-shrink: 0;
  border-radius: 2px;
  // margin-top: 2px;
  margin-bottom: 2px;
}

.cell-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
}

/* Hauptwert */
.cell-value {
  font-size: 14px;
  font-weight: 300;
  line-height: 1.2;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.125rem;
  color: var(--color-foreground);
  font-variant-numeric: tabular-nums;
  min-width: 2.5rem;
}




.cell-value.cell-empty {
  color: var(--bs-secondary-color);
  font-weight: 400;
}

.value-unit {
  font-size: 0.5rem;
  font-weight: 300;
  color: var(--color-semi-muted-foreground);
}

/* Meta-Info */
.cell-meta {
  font-size: 10px;
  color: var(--color-semi-muted-foreground);
  line-height: 1;
}

/* Delta */
.cell-delta {
  font-size: 10px;
  font-weight: 300;
  line-height: 1;
  display: flex;
  align-items: center;
  gap: 2px;
}

.delta-triangle {
  flex-shrink: 0;
}

.triangle-up {
  color: var(--default-status-green);
  fill: var(--default-status-green);
}

.triangle-down {
  color: var(--default-status-red);
  fill: var(--default-status-red);
  transform: rotate(180deg);
}

.delta-positive {
  color: var(--default-status-green);
}

.delta-negative {
  color: var(--default-status-red);
}

.delta-neutral {
  color: var(--bs-secondary-color);
}

/* Highlighted State */
.matrix-cell.is-highlighted {
  border-color: var(--target-primary);
}

.matrix-cell.is-highlighted .cell-value {
  font-size: 1.375rem;
  font-weight: 700;
}

.matrix-cell.is-highlighted .value-unit {
  font-size: 0.875rem;
}
</style>
